/**
 * Factoring Service Types
 */

import type { EcapitalRating } from '@numeo/types-dispatcher-ui'

export enum FactoringProvider {
    RTS = 'rts',
    TRIUMPH = 'triumph',
    TRIUMPH_SFTP = 'triumph_sftp',
    WEX = 'wex',
    WINFACTOR = 'winfactor',
    GSQUARED = 'gsquared',
    CAPITAL_DEPOT = 'capital_depot',
    COMPASS = 'compass',
    TAFS = 'tafs',
    ENGAGED_FINANCIAL = 'engaged_financial',
    HAUL_PAY = 'haul_pay',
    STRATOPAY = 'stratopay',
    OTR_SOLUTIONS = 'otr_solutions',
    OUTGO = 'outgo',
    ARTISPRO = 'artispro',
    ENGLAND = 'england',
    ECAPITAL = 'ecapital',
    FLEXENT = 'flexent',
    BASICBLOCK = 'basicblock',
}

export interface RTSAuthStatus {
    isAuthenticated: boolean
    hasValidToken: boolean
    canRefresh: boolean
    tokenExpiration?: Date
    lastLoginAt?: Date
    status: 'authenticated' | 'expired' | 'refresh_needed' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface TriumphAuthStatus {
    isAuthenticated: boolean
    hasValidToken: boolean
    canRefresh: boolean
    tokenExpiration?: Date
    lastLoginAt?: Date
    status: 'authenticated' | 'expired' | 'refresh_needed' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface WexAuthStatus {
    isAuthenticated: boolean
    hasValidCookies: boolean
    status: 'authenticated' | 'expired' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface WinfactorAuthStatus {
    isAuthenticated: boolean
    hasCredentials: boolean
    status: 'authenticated' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface GsquaredAuthStatus {
    isAuthenticated: boolean
    hasCredentials: boolean
    status: 'authenticated' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface CompassAuthStatus {
    isAuthenticated: boolean
    hasCredentials: boolean
    status: 'authenticated' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface TafsAuthStatus {
    isAuthenticated: boolean
    hasCredentials: boolean
    status: 'authenticated' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface EngagedFinancialAuthStatus {
    // Public API — no auth needed, always "authenticated"
    isAuthenticated: boolean
    status: 'authenticated' | 'checking'
    message?: string
    needsReauth: false
}

export interface HaulPayAuthStatus {
    isAuthenticated: boolean
    hasCredentials: boolean
    status: 'authenticated' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface StratopayAuthStatus {
    isAuthenticated: boolean
    hasCredentials: boolean
    status: 'authenticated' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface OtrSolutionsAuthStatus {
    isAuthenticated: boolean
    hasCredentials: boolean
    status: 'authenticated' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

enum ArtisproAuthState {
    AUTHENTICATED = 'authenticated',
    NOT_AUTHENTICATED = 'not_authenticated',
    CHECKING = 'checking',
}

/**
 * @public — Artispro's auth-status shape. Orphaned when the FactoringAuthStatus union was
 * removed; kept because verifyArtisproConnection is live and this is its status type.
 */
export interface ArtisproAuthStatus {
    isAuthenticated: boolean
    hasCredentials: boolean
    status: ArtisproAuthState
    message?: string
    needsReauth: boolean
}

export interface FlexentAuthStatus {
    isAuthenticated: boolean
    hasCredentials: boolean
    status: 'authenticated' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface BasicblockAuthStatus {
    isAuthenticated: boolean
    hasCredentials: boolean
    status: 'authenticated' | 'not_authenticated' | 'checking'
    message?: string
    needsReauth: boolean
}

export interface RTSRatingResponse {
    success: boolean
    data?: {
        rating?: string
        companyName?: string
        name?: string
        daysToPay?: number
        averageDaysToPayPast60?: number
        loadsCount?: number
        invoicesIncludedTotal?: number
    }
}

export interface TriumphCreditResponse {
    success: boolean
    data?: {
        creditStatus?: 'GREEN' | 'RED' | 'NOBUY'
        fmcsaBroker?: string
        fmcsaContract?: string
        legalName?: string
        averageDaysToPay?: number
        purchasedInvoiceCount?: number
        recourseCreditLimit?: number
        nonRecourseCreditLimit?: number
        notFound?: boolean
        mcNumber?: string
    }
}

export interface WexRatingResponse {
    success: boolean
    data?: {
        status?: {
            rating: string
            title: string
            color: string
        }
        name?: string
        daysToPayAll?: {
            days: string
            payments: string
        }
        loadsCount?: string
    }
}

export interface WinfactorRatingResponse {
    success: boolean
    data?: {
        creditStatus?: string
        creditScore?: number
    }
}

export interface GsquaredRatingResponse {
    success: boolean
    data?: {
        creditStatus?: string
        creditScore?: number
    }
}

export interface CapitalDepotRatingResponse {
    success: boolean
    data?: {
        creditStatus?: string
        creditScore?: string | number
    }
}

export interface CompassRatingResponse {
    success: boolean
    data?: {
        creditRating?: string // A/B/C/D/F/N
        creditScore?: string | number
        noBuyDescription?: string // if populated → broker not approved
        daysToPayAvg?: string | number
        companyName?: string
    }
}

export interface TafsRatingResponse {
    success: boolean
    data?: {
        companyName?: string
        creditScore?: string | number
        status?: string // "Buy", "No Buy"
        availableCredit?: string | number
    }
}

export interface EngagedFinancialRatingResponse {
    success: boolean
    data?: {
        debtorId?: number
        name?: string
        noBuy?: boolean
        mcNumber?: string
        dotNumber?: string
        city?: string
        state?: string
    }
}

// HTTP response wrapper for /factoring/haul-pay/rating/:mc — the inner `data` shape mirrors
// `HaulPayRatingResponse` from @numeo/types. Kept here (instead of importing from @numeo/types)
// because the wrapper { success, data } only exists on the HTTP boundary, not in the service layer.
// All fields required (the backend never returns partial data — either it succeeds with the full
// object or returns an error response).
export interface HaulPayRatingResponse {
    success: boolean
    data: {
        creditApproved: string // e.g. "approved"
        creditAvailable: number // dollars
        creditLimit: number // dollars
        avgPay: number // days to pay
        discountRate: number // percentage
        companyName: string
    }
}

export interface StratopayRatingResponse {
    success: boolean
    data?: {
        creditStatus?: string
        creditLimit?: number
        companyName?: string
        daysToPayAvg?: number
        status?: string
    }
}

export interface OtrSolutionsRatingResponse {
    success: boolean
    data?: {
        companyName?: string
        status?: number // Integer status from OTR
        noBuy?: boolean
        creditLimit?: number // Dollars
        paymentTerms?: number // Days
        approved?: boolean // Authoritative CreditCheckAsync verdict (NOT the same as noBuy)
        approvalStatus?: string // 'alert-success' | 'alert-danger'
        approvalText?: string // 'Approved' | 'Not Approved.' — shown verbatim
        tone?: 'success' | 'warning' | 'danger' | 'muted' // backend-resolved tone (color decision)
        color?: string // backend-resolved hex
    }
}

// Outgo has no rating endpoint in Phase 1 — placeholder for type completeness
export interface OutgoRatingResponse {
    success: boolean
    data?: Record<string, unknown>
}

// Artispro has no rating endpoint (submit-only) — placeholder for type completeness
export interface ArtisproRatingResponse {
    success: boolean
    data?: Record<string, unknown>
}

// eCapital's rating, from the server: the verdict and figures the portal's "Run Credit" page shows.
export interface EcapitalRatingResponse {
    success: boolean
    data: EcapitalRating
}

// Flexent returns every field or an error response — never a partial object — so the inner fields
// are required. The backend drops the fields nothing renders (see `FlexentCreditCheckOutput`), so
// this mirrors what actually arrives, not the full Flexent wire shape.
export interface FlexentRatingResponse {
    success: boolean
    data: {
        companyName: string
        status: string // 'APPROVED' | 'DENIED' — the verdict; the only field graded on
        daysToPay: number
        city: string
        state: string
    }
}

// BasicBlock's broker check returns a whole company node or an empty result — never a partial
// object — so the inner fields are required. The backend drops everything nothing renders, including
// the five fields scoped to the calling carrier account (see `BasicblockBrokerNode`), so this mirrors
// what actually arrives rather than the full BasicBlock wire shape.
export interface BasicblockRatingResponse {
    success: boolean
    data: {
        companyName: string
        docketNumber: string // 'MC567093' — carries the prefix, unlike the MC we query by
        status: string // 'approved' | 'rejected' — the verdict; the only field graded on
        city: string
        state: string
    }
}

// Type map for provider-to-response mapping
export type ProviderResponseMap = {
    rts: RTSRatingResponse
    triumph: TriumphCreditResponse
    triumph_sftp: TriumphCreditResponse
    wex: WexRatingResponse
    winfactor: WinfactorRatingResponse
    gsquared: GsquaredRatingResponse
    capital_depot: CapitalDepotRatingResponse
    compass: CompassRatingResponse
    tafs: TafsRatingResponse
    engaged_financial: EngagedFinancialRatingResponse
    haul_pay: HaulPayRatingResponse
    stratopay: StratopayRatingResponse
    otr_solutions: OtrSolutionsRatingResponse
    outgo: OutgoRatingResponse
    artispro: ArtisproRatingResponse
    ecapital: EcapitalRatingResponse
    flexent: FlexentRatingResponse
    basicblock: BasicblockRatingResponse
    // England is a read-only status source with no broker-rating endpoint; it never reaches
    // fetchFactoringData (API_ENDPOINTS.england is empty), so it has no response shape of its own.
    england: never
}

/** Every provider-specific response accepted by the shared factoring data cache. */
export type FactoringDataResponse = ProviderResponseMap[keyof ProviderResponseMap]

// Backend API response types
export interface FactoringProviderStatus {
    connected: boolean
    isValid: boolean
    accountId?: string
    email?: string
    // ISO timestamp of the last successful invoice sync (RTS), for the "last synced …" label. Null if never.
    lastSyncedAt?: string | null
    /**
     * The provider needs a fresh login. Distinct from `isValid`: that covers any unhealthy state, this
     * one names the single state the dispatcher can act on, so the UI can offer the portal rather than
     * a generic warning.
     */
    needsSignIn?: boolean
    /** England only: when the portal was last seen signed in, as distinct from when it was last read. */
    sessionSeenAt?: string | null
    /** England only: when a scrape pass reported itself FINISHED, as distinct from its first page landing. */
    lastPassCompletedAt?: string | null
}

export interface FactoringStatusResponse {
    success: boolean
    data?: {
        rts: FactoringProviderStatus
        triumph: FactoringProviderStatus
        wex: FactoringProviderStatus
        winfactor: FactoringProviderStatus
        compass?: FactoringProviderStatus
        tafs?: FactoringProviderStatus
        engaged_financial?: FactoringProviderStatus
        gsquared?: FactoringProviderStatus
        capital_depot?: FactoringProviderStatus
        haul_pay?: FactoringProviderStatus
        stratopay?: FactoringProviderStatus
        otr_solutions?: FactoringProviderStatus
        outgo?: FactoringProviderStatus
        artispro?: FactoringProviderStatus
        england?: FactoringProviderStatus
        ecapital?: FactoringProviderStatus
        flexent?: FactoringProviderStatus
        basicblock?: FactoringProviderStatus
        factoringEnabled?: boolean
    }
}
